import { Select } from '@mantine/core';
import { IconGasStation, IconHourglassHigh } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../PageHeader.js';
import { followLink, useShell } from '../../shell-context.js';
import { errorCode } from '../personnel/source.js';
import { LoadError, TableSkeleton } from '../purchasing/table.js';
import { BunkeringPanel } from './BunkeringPanel.js';
import { ConsumptionPanel } from './ConsumptionPanel.js';
import classes from './Fuel.module.css';
import { OverviewPanel } from './OverviewPanel.js';
import { RobReconciliationPanel, robLinkOf } from './RobReconciliationPanel.js';
import type { FuelSource } from './source.js';
import { TanksPanel } from './TanksPanel.js';
import { TransfersPanel } from './TransfersPanel.js';
import { FUEL_TABS, FUEL_TYPES_PATH, fuelTabOf } from './tabs.js';
import type { FuelTanksData } from './types.js';
import { VoyageAnalysisPanel } from './VoyageAnalysisPanel.js';

export interface FuelScreenProps {
  source: FuelSource;
  /** Yol (test); verilmezse kabuğun yolu. */
  path?: string;
}

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; data: FuelTanksData };

/**
 * Fuel Management (ADR 0117 §14; ortağın tasarımı 02-tanklar.jpeg): başlık, yedi sekmeli kart ve
 * sekmenin içeriği. Gemi uygulamasında ve web ofiste aynı ekran (ADR 0019 §2); ofiste gemi seçici
 * başlığın sağında, tanklar salt okunur.
 */
export function FuelScreen({ source, path }: FuelScreenProps) {
  const { t } = useTranslation();
  const shell = useShell();
  const current = path ?? shell?.path ?? '/fuel';
  const tab = fuelTabOf(current);
  const entry = FUEL_TABS.find((e) => e.tab === tab);
  const [state, setState] = useState<State>({ kind: 'loading' });
  // Bağlantıdaki gemi (`?vesselId=`; ofiste sefer raporundan Tüketim'e; panodan `?vessel=` ve
  // düzeltme `?adjustment=`, ADR 0156)
  const [link] = useState(() => robLinkOf(shell?.search ?? ''));
  const [vesselId, setVesselId] = useState<string | null>(link.vesselId);
  // Ofiste gemi seçici: hangi sekme okuduysa onun gemi listesi (Y2 sekmeleri kendi verisini okur)
  const [picked, setPicked] = useState<{
    vessel: { id: string; name: string };
    vessels: { id: string; name: string }[];
  } | null>(null);
  const onVessel = useCallback(
    (loaded: { vessel: { id: string; name: string }; vessels?: { id: string; name: string }[] }) =>
      setPicked({ vessel: loaded.vessel, vessels: loaded.vessels ?? [] }),
    [],
  );
  const tanksTab = tab === 'tanks';

  const load = useCallback(async () => {
    if (!tanksTab) return null;
    try {
      const data = await source.tanks(vesselId);
      setState({ kind: 'ready', data });
      return data;
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
      return null;
    }
  }, [source, vesselId, tanksTab]);

  useEffect(() => {
    void load();
  }, [load]);

  const data = state.kind === 'ready' ? state.data : null;
  const shown = tanksTab && data ? { vessel: data.vessel, vessels: data.vessels ?? [] } : picked;
  const vessels = shown?.vessels ?? [];
  const aside =
    source.scope === 'web' && vessels.length > 0 && shown ? (
      <Select
        className={classes.vesselSelect}
        label={t('fuel.vessel')}
        value={shown.vessel.id}
        allowDeselect={false}
        data={vessels.map((v) => ({ value: v.id, label: v.name }))}
        onChange={(value) => {
          if (value) setVesselId(value);
        }}
      />
    ) : undefined;

  return (
    <div className={classes.screen}>
      <PageHeader
        title={t('fuel.title')}
        icon={<IconGasStation size={30} stroke={1.8} />}
        {...(aside ? { aside } : {})}
      />
      <nav className={classes.tabsCard} aria-label={t('fuel.tabsLabel')}>
        <ul className={classes.tabs}>
          {FUEL_TABS.map((item) => (
            <li key={item.tab}>
              <a
                href={item.path}
                className={classes.tab}
                aria-current={item.tab === tab ? 'page' : undefined}
                onClick={(event) => {
                  if (shell) followLink(event, item.path, shell.navigate);
                }}
              >
                {t(`fuel.tabs.${item.tab}`)}
              </a>
            </li>
          ))}
        </ul>
      </nav>
      {tab === 'overview' ? (
        <OverviewPanel source={source} vesselId={vesselId} onVessel={onVessel} />
      ) : tab === 'bunkering' ? (
        <BunkeringPanel source={source} vesselId={vesselId} onVessel={onVessel} />
      ) : tab === 'transfers' ? (
        <TransfersPanel source={source} vesselId={vesselId} onVessel={onVessel} />
      ) : tab === 'consumption' ? (
        <ConsumptionPanel source={source} vesselId={vesselId} onVessel={onVessel} />
      ) : tab === 'rob' && source.reconciliation ? (
        <RobReconciliationPanel
          source={source}
          vesselId={vesselId}
          adjustmentId={link.adjustmentId}
          onVessel={onVessel}
        />
      ) : tab === 'voyage-analysis' && source.analysis ? (
        <VoyageAnalysisPanel
          source={source}
          vesselId={vesselId}
          onVessel={onVessel}
          onPickVessel={setVesselId}
        />
      ) : entry && entry.stage !== null ? (
        <section className={classes.card} aria-label={t(`fuel.tabs.${tab}`)}>
          <div className={classes.soon}>
            <IconHourglassHigh size={36} stroke={1.6} className={classes.soonIcon} aria-hidden />
            <h2 className={classes.soonTitle}>
              {t('fuel.comingSoon.title', { tab: t(`fuel.tabs.${tab}`) })}
            </h2>
            <p className={classes.soonText}>{t(`fuel.comingSoon.${tab}`)}</p>
            <p className={classes.soonText}>{t('fuel.comingSoon.body', { stage: entry.stage })}</p>
          </div>
        </section>
      ) : state.kind === 'loading' ? (
        <TableSkeleton columns={3} rows={6} label={t('fuel.tanks.listLabel')} />
      ) : state.kind === 'error' ? (
        <LoadError code={state.code} onRetry={() => void load()} title={t('fuel.title')} />
      ) : (
        <TanksPanel
          source={source}
          data={state.data}
          subtab={current.startsWith(FUEL_TYPES_PATH) ? 'fuelTypes' : 'definitions'}
          onReload={load}
        />
      )}
    </div>
  );
}
